import React, { useState, useEffect, useCallback } from 'react';
import { X, Phone, Check, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Countries list
const countries = [
  { code: 'fr', dial: '+33', name: 'France' },
  { code: 'be', dial: '+32', name: 'Belgique' },
  { code: 'ch', dial: '+41', name: 'Suisse' },
  { code: 'lu', dial: '+352', name: 'Luxembourg' },
  { code: 'mc', dial: '+377', name: 'Monaco' },
  { code: 'de', dial: '+49', name: 'Allemagne' },
  { code: 'es', dial: '+34', name: 'Espagne' },
  { code: 'it', dial: '+39', name: 'Italie' },
  { code: 'pt', dial: '+351', name: 'Portugal' },
  { code: 'gb', dial: '+44', name: 'Royaume-Uni' },
  { code: 'us', dial: '+1', name: 'États-Unis' },
  { code: 'ca', dial: '+1', name: 'Canada' },
  { code: 'nl', dial: '+31', name: 'Pays-Bas' },
  { code: 'at', dial: '+43', name: 'Autriche' },
  { code: 'ie', dial: '+353', name: 'Irlande' },
  { code: 'pl', dial: '+48', name: 'Pologne' },
  { code: 'ma', dial: '+212', name: 'Maroc' },
  { code: 'tn', dial: '+216', name: 'Tunisie' }
];

const ExitIntentPopup = ({ 
  userId, 
  settings = {},
  language = 'fr',
  onClose,
  forceShow = false 
}) => {
  const [isVisible, setIsVisible] = useState(forceShow);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    popup_title_fr = 'Avant de partir...',
    popup_title_en = 'Before you go...',
    popup_message_fr = 'Souhaitez-vous être rappelé gratuitement par notre équipe ?',
    popup_message_en = 'Would you like a free callback from our team?',
    popup_cta_fr = 'Être rappelé',
    popup_cta_en = 'Request callback',
    popup_legal_fr = 'J\'accepte d\'être recontacté par téléphone.',
    popup_legal_en = 'I agree to be contacted by phone.',
    primary_color = '#3272F1',
    trigger_once_per_session = true,
    inactivity_timeout_seconds = 30
  } = settings;

  const title = language === 'fr' ? popup_title_fr : popup_title_en;
  const message = language === 'fr' ? popup_message_fr : popup_message_en;
  const ctaText = language === 'fr' ? popup_cta_fr : popup_cta_en;
  const legalText = language === 'fr' ? popup_legal_fr : popup_legal_en;
  const phonePlaceholder = language === 'fr' ? '06 12 34 56 78' : '555 123 4567';
  const errorInvalidPhone = language === 'fr' ? 'Numéro de téléphone invalide' : 'Invalid phone number';
  const errorConsent = language === 'fr' ? 'Vous devez accepter d\'être recontacté' : 'You must agree to be contacted';
  const thankYouText = language === 'fr' ? 'Merci !' : 'Thank you!';
  const confirmText = language === 'fr' ? 'Nous vous rappellerons très bientôt.' : 'We will call you back very soon.';

  const showPopup = useCallback(() => {
    if (trigger_once_per_session && hasTriggered) return;
    if (sessionStorage.getItem(`exitflow-shown-${userId}`)) return;
    
    setIsVisible(true);
    setHasTriggered(true);
    if (trigger_once_per_session) {
      sessionStorage.setItem(`exitflow-shown-${userId}`, 'true');
    }
  }, [userId, trigger_once_per_session, hasTriggered]);

  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      return;
    }

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        showPopup();
      }
    };

    let inactivityTimer;
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        showPopup();
      }, inactivity_timeout_seconds * 1000);
    };

    const handlePopState = () => {
      showPopup();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        showPopup();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', resetInactivityTimer);
    document.addEventListener('keydown', resetInactivityTimer);
    document.addEventListener('scroll', resetInactivityTimer);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    resetInactivityTimer();

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', resetInactivityTimer);
      document.removeEventListener('keydown', resetInactivityTimer);
      document.removeEventListener('scroll', resetInactivityTimer);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(inactivityTimer);
    };
  }, [showPopup, forceShow, inactivity_timeout_seconds]);

  const validatePhone = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, '');
    return cleaned.length >= 6 && cleaned.length <= 15;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePhone(phone)) {
      setError(errorInvalidPhone);
      return;
    }

    if (!consent) {
      setError(errorConsent);
      return;
    }

    setLoading(true);

    // Format phone with country code
    let formattedPhone = phone.replace(/[\s\-\(\)]/g, '');
    if (!formattedPhone.startsWith('+')) {
      if (formattedPhone.startsWith('0')) {
        formattedPhone = formattedPhone.substring(1);
      }
      formattedPhone = selectedCountry.dial + formattedPhone;
    }

    try {
      await axios.post(`${API}/api/widget/callback/${userId}`, {
        phone: formattedPhone,
        page_url: window.location.href,
        consent: consent,
        language: language,
        user_agent: navigator.userAgent
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const getFlagUrl = (code) => `https://flagcdn.com/24x18/${code}.png`;

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
      onClick={handleClose}
      data-testid="exit-intent-overlay"
    >
      <div 
        className="relative w-full max-w-[360px] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
        style={{ 
          animation: 'fadeInUp 0.3s ease-out',
          background: 'linear-gradient(180deg, #1f1f23 0%, #18181b 100%)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        {/* Close button */}
        <div className="flex justify-end p-3 pb-0">
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-800 transition-colors text-gray-500 hover:text-gray-300"
            data-testid="popup-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          {!success ? (
            <>
              {/* Header */}
              <div className="text-center mb-6">
                <div 
                  className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${primary_color}20` }}
                >
                  <Phone className="w-7 h-7" style={{ color: primary_color }} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">
                  {title}
                </h2>
                <p className="text-gray-400 text-sm">
                  {message}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Phone input with country selector */}
                <div 
                  className="flex rounded-xl overflow-hidden transition-colors"
                  style={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                >
                  {/* Country selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="flex items-center gap-2 px-3 py-3 transition-colors h-full"
                      style={{ backgroundColor: '#3f3f46' }}
                    >
                      <img 
                        src={getFlagUrl(selectedCountry.code)} 
                        alt={selectedCountry.name}
                        className="w-6 h-4 rounded-sm object-cover"
                      />
                      <span className="text-gray-300 text-sm font-medium">{selectedCountry.dial}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown */}
                    {showDropdown && (
                      <div 
                        className="absolute top-full left-0 mt-1 w-56 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto"
                        style={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                      >
                        {countries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors"
                            style={{ 
                              backgroundColor: selectedCountry.code === country.code ? '#3f3f46' : 'transparent'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#3f3f46'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = selectedCountry.code === country.code ? '#3f3f46' : 'transparent'}
                          >
                            <img 
                              src={getFlagUrl(country.code)} 
                              alt={country.name}
                              className="w-6 h-4 rounded-sm object-cover"
                            />
                            <span className="text-gray-300 text-sm flex-1 text-left">{country.name}</span>
                            <span className="text-gray-500 text-sm">{country.dial}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Phone input */}
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={phonePlaceholder}
                    className="flex-1 px-3 py-3 bg-transparent text-white placeholder-gray-500 focus:outline-none text-base"
                    data-testid="phone-input"
                  />
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="sr-only"
                      data-testid="consent-checkbox"
                    />
                    <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${
                      consent 
                        ? 'border-transparent' 
                        : 'border-gray-600 group-hover:border-gray-500'
                    }`} style={{ backgroundColor: consent ? primary_color : 'transparent' }}>
                      {consent && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 leading-relaxed">
                    {legalText}
                  </span>
                </label>

                {/* Error message */}
                {error && (
                  <p className="text-red-400 text-xs text-center py-1" data-testid="error-message">
                    {error}
                  </p>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-6 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50 text-base"
                  style={{ backgroundColor: primary_color }}
                  data-testid="submit-btn"
                >
                  {loading ? '...' : ctaText}
                </button>
              </form>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-emerald-500/20">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {thankYouText}
              </h2>
              <p className="text-gray-400 text-sm">
                {confirmText}
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ExitIntentPopup;
